/**
 * `/api/messages` — every call the messaging module makes.
 *
 * In the module's folder rather than `src/api/`, so the module is one slice;
 * it still goes through `apiFetch`, so the 401 handling, the error shape and the
 * relative path are core's. No function here takes the reader's user id — the
 * person is the session, on the server.
 */
import type {
  ListQueryParams,
  MessageListResponse,
  MessageRecipientSearchResponse,
  MessageResponse,
  MessageStateResponse,
  MessageUnreadCountResponse,
  SendMessageRequest,
  SubjectType,
} from '@forsys/shared';
import { apiFetch, query } from '../../api/client';

export type MessageBox = 'inbox' | 'archived' | 'sent';

export interface MessageListParams extends ListQueryParams {
  box?: MessageBox;
  /** Inbox and archive only. */
  unread?: boolean;
}

export function listMessages({ box = 'inbox', unread, ...page }: MessageListParams = {}): Promise<MessageListResponse> {
  if (box === 'sent') return apiFetch<MessageListResponse>(`/messages/sent${query({ ...page })}`);
  return apiFetch<MessageListResponse>(
    `/messages${query({ ...page, archived: box === 'archived' ? true : undefined, unread: unread ? true : undefined })}`,
  );
}

export function getMessage(id: number): Promise<MessageResponse> {
  return apiFetch<MessageResponse>(`/messages/${id}`);
}

export async function sendMessage(request: SendMessageRequest): Promise<MessageResponse> {
  return apiFetch<MessageResponse>('/messages', { method: 'POST', json: request });
}

export function searchRecipients(
  q: string,
  options: { type?: SubjectType; limit?: number } = {},
): Promise<MessageRecipientSearchResponse> {
  return apiFetch<MessageRecipientSearchResponse>(`/messages/recipients${query({ q, ...options })}`);
}

/** The badge. Polled, so the smallest call there is. */
export function unreadMessageCount(): Promise<MessageUnreadCountResponse> {
  return apiFetch<MessageUnreadCountResponse>('/messages/unread-count');
}

async function changeState(id: number, action: 'read' | 'unread' | 'archive' | 'unarchive'): Promise<MessageStateResponse> {
  const result = await apiFetch<MessageStateResponse>(`/messages/${id}/${action}`, { method: 'POST' });
  announce(result.unreadCount);
  return result;
}

export const markRead = (id: number) => changeState(id, 'read');
export const markUnread = (id: number) => changeState(id, 'unread');
export const archive = (id: number) => changeState(id, 'archive');
export const unarchive = (id: number) => changeState(id, 'unarchive');

// --- telling the badge -------------------------------------------------------

/**
 * Every state change answers with the new unread count; this passes it to the
 * header badge at once, rather than leaving it a poll interval out of date
 * right after the reader opened the message it was counting.
 *
 * A DOM event rather than shared state: the badge and the pages share no parent
 * below the router, and this is one number.
 */
const CHANGED = 'forsys:messaging:unread';

function announce(unreadCount: number): void {
  window.dispatchEvent(new CustomEvent<number>(CHANGED, { detail: unreadCount }));
}

/** Calls `listener` with the new unread count. Returns the unsubscribe. */
export function onUnreadChanged(listener: (unreadCount: number) => void): () => void {
  const handler = (event: Event) => listener((event as CustomEvent<number>).detail);
  window.addEventListener(CHANGED, handler);
  return () => window.removeEventListener(CHANGED, handler);
}
